import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, ArrowRight, Check, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANDS = {
  'smithers-xi': {
    name: 'SMITHERS XI',
    tagline: 'The Future of Content Protection',
    subtitle: 'AI-Powered IP Enforcement. Autonomous Revenue Recovery. Total Creator Control.',
    gradient: 'from-amber-500 via-yellow-600 to-amber-900',
    accentColor: '#D4AF37',
    glowColor: 'rgba(212,175,55,0.3)',
    bgFrom: '#0a0803',
    bgTo: '#1a150a',
  },
  'creators-agent': {
    name: "THE CREATOR'S AGENT",
    tagline: 'Build Your Own AI Agent — The Right Way',
    subtitle: 'Foundation to Genesis. The A-Z Agent Build System for serious creators and entrepreneurs.',
    gradient: 'from-cyan-500 via-blue-600 to-cyan-900',
    accentColor: '#00F0FF',
    glowColor: 'rgba(0,240,255,0.3)',
    bgFrom: '#030810',
    bgTo: '#0a0f1a',
  },
  'creators-stake': {
    name: "THE CREATOR'S STAKE",
    tagline: 'Reserve Your Seat',
    subtitle: 'The definitive platform for creators who want to own, protect, and monetize their IP.',
    gradient: 'from-pink-500 via-fuchsia-600 to-magenta-700',
    accentColor: '#FF00E5',
    glowColor: 'rgba(255,0,229,0.3)',
    bgFrom: '#0a0310',
    bgTo: '#150a1a',
  },
  'quadra-core': {
    name: 'QUADRA Core OS',
    tagline: 'The Operating System for the New Internet',
    subtitle: 'Adaptive bandwidth. Intelligent routing. Enterprise-grade infrastructure for the decentralized web.',
    gradient: 'from-indigo-500 via-violet-600 to-blue-900',
    accentColor: '#6366F1',
    glowColor: 'rgba(99,102,241,0.3)',
    bgFrom: '#050310',
    bgTo: '#0a081a',
  },
};

export default function BrandLandingPage() {
  const { brand } = useParams();
  const config = BRANDS[brand] || BRANDS['smithers-xi'];
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), brand: brand || 'smithers-xi' }),
      });
      const data = await res.json();
      if (data.status === 'registered' || data.status === 'already_registered') {
        setSubmitted(true);
        setPosition(data.position || null);
      } else {
        setError('Something went wrong. Try again.');
      }
    } catch (err) {
      setError('Connection failed. Try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${config.bgFrom}, ${config.bgTo})` }}
      data-testid={`brand-landing-${brand}`}>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[600px] h-[600px] rounded-full opacity-[0.04] blur-3xl"
          style={{ background: config.accentColor, top: '-200px', right: '-200px' }} />
        <div className="absolute w-[400px] h-[400px] rounded-full opacity-[0.03] blur-3xl"
          style={{ background: config.accentColor, bottom: '-100px', left: '-100px' }} />
        {/* Grid lines */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id={`grid-${brand}`} width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke={config.accentColor} strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#grid-${brand})`} />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-lg w-full px-6 text-center">
        {/* Lock icon */}
        <div className="mb-8 flex justify-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: `${config.accentColor}10`, border: `1px solid ${config.accentColor}30` }}>
            <Lock className="w-7 h-7" style={{ color: config.accentColor }} />
          </div>
        </div>

        {/* Brand name */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 tracking-wider"
          style={{ fontFamily: 'Orbitron', color: config.accentColor, textShadow: `0 0 40px ${config.glowColor}` }}
          data-testid="brand-name">
          {config.name}
        </h1>

        {/* Tagline */}
        <p className="text-lg sm:text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>
          {config.tagline}
        </p>

        {/* Subtitle */}
        <p className="text-sm text-gray-400 mb-10 max-w-md mx-auto leading-relaxed">
          {config.subtitle}
        </p>

        {/* Form or Success */}
        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="waitlist-form">
            <input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="waitlist-name"
              className="w-full px-5 py-3.5 rounded-xl text-sm bg-white/[0.04] border text-white placeholder-gray-500 focus:outline-none transition-all"
              style={{ borderColor: `${config.accentColor}20`, focusBorderColor: config.accentColor }}
              onFocus={(e) => e.target.style.borderColor = `${config.accentColor}60`}
              onBlur={(e) => e.target.style.borderColor = `${config.accentColor}20`}
            />
            <input
              type="email"
              placeholder="Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="waitlist-email"
              className="w-full px-5 py-3.5 rounded-xl text-sm bg-white/[0.04] border text-white placeholder-gray-500 focus:outline-none transition-all"
              style={{ borderColor: `${config.accentColor}20` }}
              onFocus={(e) => e.target.style.borderColor = `${config.accentColor}60`}
              onBlur={(e) => e.target.style.borderColor = `${config.accentColor}20`}
            />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="waitlist-submit"
              className="w-full py-3.5 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{
                background: config.accentColor,
                color: '#000',
                boxShadow: `0 0 30px ${config.glowColor}`,
                fontFamily: 'Orbitron',
                letterSpacing: '0.1em',
              }}
            >
              {submitting ? 'RESERVING...' : 'RESERVE YOUR SEAT'}
              {!submitting && <ArrowRight size={16} />}
            </button>
          </form>
        ) : (
          <div className="rounded-2xl p-8 text-center" data-testid="waitlist-success"
            style={{ background: `${config.accentColor}08`, border: `1px solid ${config.accentColor}20` }}>
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: `${config.accentColor}15` }}>
              <Check className="w-7 h-7" style={{ color: config.accentColor }} />
            </div>
            <h3 className="text-lg font-black text-white mb-2" style={{ fontFamily: 'Orbitron' }}>YOU'RE IN</h3>
            <p className="text-sm text-gray-400">
              Seat reserved. We'll notify you at <span className="text-white font-bold">{email}</span> when we go live.
            </p>
            {position && (
              <div className="mt-4 text-xs text-gray-500">
                Position <span className="font-bold" style={{ color: config.accentColor }}>#{position}</span> on the list
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 flex items-center justify-center gap-2 text-[10px] text-gray-600">
          <Shield size={10} />
          <span>Your information is secure and will never be shared</span>
        </div>

        {/* Brand footer */}
        <div className="mt-6 text-[9px] text-gray-700 tracking-widest uppercase" style={{ fontFamily: 'Orbitron' }}>
          A VIG Brands Company
        </div>
      </div>
    </div>
  );
}
